import { useState, useEffect, useRef } from "react";
import { PlatformIcon } from "@/components/PlatformIcon";

const hostInitials = ["A", "R", "S"];

type TabKey = "sessions" | "team" | "scheduling" | "events" | "studios";

const tabs: { key: TabKey; label: string; count?: string }[] = [
  { key: "sessions", label: "Sessions", count: "15" },
  { key: "team", label: "Team", count: "355" },
  { key: "scheduling", label: "Scheduling" },
  { key: "events", label: "Events" },
  { key: "studios", label: "Studios" },
];

/* ─── Live Indicator ─── */
function LiveDot() {
  return (
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
    </span>
  );
}

/* ─── Animated counter ─── */
function AnimatedCount({ target, duration = 1200 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<number>();

  useEffect(() => {
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setCount(Math.round(target * progress));
      if (progress < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => { if (ref.current) cancelAnimationFrame(ref.current); };
  }, [target, duration]);

  return <>{count}</>;
}

/* ─── Typing animation for search ─── */
function TypingSearch({ text }: { text: string }) {
  const [displayed, setDisplayed] = useState("");
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setDisplayed(text.slice(0, index + 1));
        setIndex(i => i + 1);
      }, 80 + Math.random() * 60);
      return () => clearTimeout(timeout);
    }
    // Reset after pause
    const reset = setTimeout(() => {
      setDisplayed("");
      setIndex(0);
    }, 2500);
    return () => clearTimeout(reset);
  }, [index, text]);

  return (
    <span className="text-[11px] text-foreground/70">
      {displayed}
      <span className="inline-block w-px h-3 bg-foreground/50 ml-px animate-pulse" />
    </span>
  );
}

/* ─── Fake cursor that highlights a card ─── */
function useFakeCursor(totalItems: number, interval = 3000) {
  const [highlighted, setHighlighted] = useState(-1);

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      setHighlighted(i % totalItems);
      i++;
    }, interval);
    // Start after a short delay
    const start = setTimeout(() => setHighlighted(0), 1500);
    return () => { clearInterval(timer); clearTimeout(start); };
  }, [totalItems, interval]);

  return highlighted;
}

/* ─── Subtle notification toast — shows once then fades ─── */
function FloatingNotification({ hidden }: { hidden?: boolean }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const show = setTimeout(() => setVisible(true), 5000);
    const hide = setTimeout(() => setVisible(false), 9000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []);

  return (
    <div className={`absolute top-3 right-3 z-20 flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card/90 shadow-sm transition-all duration-500 ${
      visible && !hidden ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
    }`}>
      <span className="text-[11px]">🔴</span>
      <span className="text-[10px] text-muted-foreground">Alya Putri went live in Studio A</span>
    </div>
  );
}

/* ─── Sessions Tab ─── */
const sessionsData = [
  { day: "MON", date: 23, sessions: 5, hours: "9.5h", cards: [
    { time: "09:00–11:00", title: "Ramadan Sale", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 0, live: true, platforms: ["tiktok", "shopee"] },
    { time: "12:00–14:00", title: "Flash Sale March", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 1, platforms: ["tiktok"] },
    { time: "13:00–15:00", title: "Beauty Essentials", brand: "Wardah", studio: "Studio B", hasHost: true, avatar: 2, platforms: ["shopee"] },
  ]},
  { day: "TUE", date: 24, sessions: 5, hours: "9.5h", cards: [
    { time: "09:00–11:00", title: "Ramadan Sale", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 1, platforms: ["tiktok", "shopee"] },
    { time: "12:00–14:00", title: "Flash Sale March", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 2, platforms: ["shopee"] },
  ]},
  { day: "WED", date: 25, sessions: 5, hours: "9.5h", cards: [
    { time: "09:00–11:00", title: "Ramadan Sale", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 0, platforms: ["tiktok"] },
    { time: "13:00–15:00", title: "Beauty Essentials", brand: "Wardah", studio: "Studio B", hasHost: false, avatar: -1, platforms: ["tiktok", "shopee"] },
  ]},
  { day: "THU", date: 26, sessions: 5, hours: "9.5h", cards: [
    { time: "09:00–11:00", title: "Ramadan Sale", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 2, platforms: ["shopee"] },
    { time: "12:00–14:00", title: "Flash Sale March", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 0, platforms: ["tiktok"] },
  ]},
  { day: "FRI", date: 27, sessions: 5, hours: "9.5h", cards: [
    { time: "09:00–11:00", title: "Ramadan Sale", brand: "Wardah", studio: "Studio A", hasHost: false, avatar: -1, platforms: ["tiktok", "shopee"] },
    { time: "12:00–14:00", title: "Flash Sale March", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 1, platforms: ["shopee"] },
  ]},
  { day: "SAT", date: 28, sessions: 3, hours: "5.5h", cards: [
    { time: "09:00–11:00", title: "Ramadan Sale", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 2, platforms: ["tiktok"] },
    { time: "13:00–15:00", title: "Beauty Essentials", brand: "Wardah", studio: "Studio B", hasHost: true, avatar: 0, platforms: ["tiktok", "shopee"] },
  ]},
  { day: "SUN", date: 29, sessions: 2, hours: "3.5h", cards: [
    { time: "09:00–11:00", title: "Flash Sale March", brand: "Wardah", studio: "Studio C", hasHost: false, avatar: -1, platforms: ["shopee"] },
  ]},
];

// Count all cards across all days
const allSessionCards = sessionsData.flatMap(d => d.cards);

const aiGeneratedSessions = [
  { day: "MON", date: 23, cards: [
    { time: "09:00–11:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 0, platforms: ["tiktok"] as string[] },
    { time: "12:00–14:00", title: "Extended Session", brand: "Wardah", studio: "Studio B", hasHost: true, avatar: 1, platforms: ["shopee"] as string[] },
    { time: "15:00–17:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 2, platforms: ["tiktok"] as string[] },
  ]},
  { day: "TUE", date: 24, cards: [
    { time: "09:00–11:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 1, platforms: ["shopee"] as string[] },
    { time: "12:00–14:00", title: "Extended Session", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 0, platforms: ["tiktok"] as string[] },
    { time: "15:00–17:00", title: "Extended Session", brand: "Wardah", studio: "Studio B", hasHost: true, avatar: 2, platforms: ["tiktok", "shopee"] as string[] },
  ]},
  { day: "WED", date: 25, cards: [
    { time: "09:00–11:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 2, platforms: ["tiktok"] as string[] },
    { time: "12:00–14:00", title: "Extended Session", brand: "Wardah", studio: "Studio B", hasHost: false, avatar: -1, platforms: ["shopee"] as string[] },
    { time: "15:00–17:00", title: "Extended Session", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 1, platforms: ["tiktok"] as string[] },
  ]},
  { day: "THU", date: 26, cards: [
    { time: "09:00–11:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 0, platforms: ["shopee"] as string[] },
    { time: "12:00–14:00", title: "Extended Session", brand: "Wardah", studio: "Studio B", hasHost: true, avatar: 2, platforms: ["tiktok"] as string[] },
    { time: "15:00–17:00", title: "Extended Session", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 1, platforms: ["tiktok", "shopee"] as string[] },
  ]},
  { day: "FRI", date: 27, cards: [
    { time: "09:00–11:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 1, platforms: ["tiktok"] as string[] },
    { time: "12:00–14:00", title: "Extended Session", brand: "Wardah", studio: "Studio B", hasHost: true, avatar: 0, platforms: ["shopee"] as string[] },
    { time: "15:00–17:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 2, platforms: ["tiktok"] as string[] },
  ]},
  { day: "SAT", date: 28, cards: [
    { time: "09:00–11:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 2, platforms: ["tiktok", "shopee"] as string[] },
    { time: "12:00–14:00", title: "Extended Session", brand: "Wardah", studio: "Studio B", hasHost: true, avatar: 1, platforms: ["shopee"] as string[] },
    { time: "15:00–17:00", title: "Extended Session", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 0, platforms: ["tiktok"] as string[] },
  ]},
  { day: "SUN", date: 29, cards: [
    { time: "09:00–11:00", title: "Extended Session", brand: "Wardah", studio: "Studio C", hasHost: true, avatar: 0, platforms: ["shopee"] as string[] },
    { time: "12:00–14:00", title: "Extended Session", brand: "Wardah", studio: "Studio A", hasHost: true, avatar: 2, platforms: ["tiktok"] as string[] },
    { time: "15:00–17:00", title: "Extended Session", brand: "Wardah", studio: "Studio B", hasHost: false, avatar: -1, platforms: ["tiktok", "shopee"] as string[] },
  ]},
];

function SessionsContent({ scheduleGenerated }: { scheduleGenerated?: boolean }) {
  const data = scheduleGenerated ? aiGeneratedSessions : sessionsData;
  const allCards = data.flatMap(d => d.cards);
  const highlighted = useFakeCursor(allCards.length, 2500);
  let cardIndex = 0;

  return (
    <div className="flex-1 flex flex-col">
      {/* Stats bar */}
      <div className="border-b border-border px-3 sm:px-4 py-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {scheduleGenerated ? (
            <>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[10px]">✦</span>
                <span className="text-[10px] font-medium text-success whitespace-nowrap">AI Schedule Active</span>
              </div>
              <div className="min-w-0 flex-1 text-[10px] leading-tight text-muted-foreground">
                <AnimatedCount target={21} /> sessions · <AnimatedCount target={3} /> hosts · <AnimatedCount target={42} duration={1800} />h this week
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5 shrink-0">
                <LiveDot />
                <span className="text-[10px] font-medium text-success whitespace-nowrap">2 Live Now</span>
              </div>
              <div className="min-w-0 flex-1 text-[10px] leading-tight text-muted-foreground">
                <AnimatedCount target={15} /> upcoming · <AnimatedCount target={4} /> hosts active · <AnimatedCount target={56} duration={1800} />.5h this week
              </div>
            </>
          )}
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-1 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {[
              { key: "all", label: "All" },
              { key: "tiktok", label: "TikTok" },
              { key: "shopee", label: "Shopee" },
            ].map((f) => (
              <span key={f.key} className={`shrink-0 inline-flex items-center gap-1 text-[9px] px-2 py-1 rounded-full transition-colors ${
                f.key === "all" ? "bg-foreground text-background font-medium" : "text-muted-foreground hover:bg-accent"
              }`}>
                {f.key !== "all" && <PlatformIcon type={f.key} size={11} />}
                {f.label}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-7 divide-x divide-border">
        {data.map((col) => (
          <div key={col.day} className="flex flex-col min-w-0">
            <div className="px-2 pt-3 pb-2 text-center border-b border-border">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{col.day}</span>
              <div className="text-[20px] font-medium text-foreground leading-tight mt-0.5">{col.date}</div>
              <span className="text-[9px] text-muted-foreground">{col.cards.length} sessions · {scheduleGenerated ? "6h" : ("hours" in col ? String((col as { hours?: unknown }).hours) : "6h")}</span>
            </div>
            <div className="flex-1 p-1.5 space-y-1.5">
              {col.cards.map((card, ci) => {
                const thisIndex = cardIndex++;
                const isHighlighted = highlighted === thisIndex;
                const isNew = scheduleGenerated && card.hasHost;
                const isDraft = !card.hasHost;
                return (
                  <div
                    key={ci}
                    className={`rounded-lg border p-2 text-[10px] transition-all duration-500 ${
                      isDraft
                        ? "border-destructive/30 bg-destructive/5"
                        : isNew
                        ? "border-success/40 bg-success/5 animate-fade-in"
                        : isHighlighted ? "border-primary/50 bg-primary/5 shadow-md shadow-primary/10 scale-[1.02]"
                        : "border-border bg-card"
                    }`}
                    style={isNew ? { animationDelay: `${thisIndex * 80}ms`, animationFillMode: "backwards" } : undefined}
                  >
                    <div className="flex items-start justify-between gap-0.5 mb-1">
                      <span className="font-medium text-foreground/80 shrink-0">{card.time}</span>
                      {isDraft ? (
                        <span className="text-[7px] sm:text-[8px] uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded-full bg-warning/20 text-warning font-medium whitespace-nowrap leading-tight">Draft</span>
                      ) : isNew ? (
                        <span className="text-[7px] sm:text-[8px] uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded-full bg-success/20 text-success font-medium flex items-center gap-0.5 whitespace-nowrap leading-tight">
                          <span className="text-[9px] sm:text-[10px]">✦</span> AI
                        </span>
                      ) : ("live" in card && (card as { live?: unknown }).live) ? (
                        <span className="flex items-center gap-0.5 text-[7px] sm:text-[8px] uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded-full bg-destructive/20 text-destructive font-medium whitespace-nowrap leading-tight">
                          <LiveDot /> Live
                        </span>
                      ) : (
                        <span className="text-[7px] sm:text-[8px] uppercase tracking-wider px-1 sm:px-1.5 py-0.5 rounded-full bg-success/20 text-success font-medium whitespace-nowrap leading-tight">Confirmed</span>
                      )}
                    </div>
                    <div className="font-medium text-foreground truncate">{card.title}</div>
                    <div className="text-muted-foreground truncate">{card.brand}</div>
                    <div className="text-muted-foreground/70 truncate mt-0.5">📍 {card.studio}</div>
                    <div className="mt-1.5 flex items-center justify-between">
                      {card.hasHost ? (
                        <div className="h-5 w-5 rounded-full bg-muted-foreground/15 flex items-center justify-center text-[9px] font-medium text-muted-foreground">
                          {hostInitials[card.avatar] ?? "H"}
                        </div>
                      ) : (
                        <span className="text-[9px] text-destructive font-medium">⚠ No host</span>
                      )}
                      <div className="flex items-center gap-0.5">
                        {card.platforms.map(p => <PlatformIcon key={p} type={p} size={12} />)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* AI generated banner */}
      {scheduleGenerated && (
        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-10 animate-fade-in" style={{ animationDelay: "600ms", animationFillMode: "backwards" }}>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-success/30 bg-card/95 shadow-lg backdrop-blur-sm">
            <span className="text-[10px]">✦</span>
            <span className="text-[10px] text-muted-foreground">21 sessions generated by AI · 3 hosts · 42h total</span>
            <span className="text-[10px] font-medium text-success">Applied</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Team Tab ─── */
const teamData = [
  { name: "Alya Putri", avatar: -1, initial: "A", platforms: ["tiktok", "shopee"], status: "Available", online: true },
  { name: "Ani Cahyani", avatar: -1, initial: "A", platforms: ["tiktok", "shopee"], status: "Available" },
  { name: "Ani Cahyani B", avatar: -1, initial: "A", platforms: ["tiktok"], status: "Available" },
  { name: "Ani Cahyani D", avatar: -1, initial: "A", platforms: ["shopee"], status: "Available" },
  { name: "Ani Cahyani F", avatar: -1, initial: "A", platforms: ["tiktok", "shopee"], status: "Available" },
  { name: "Ani Kusuma A", avatar: -1, initial: "A", platforms: ["shopee"], status: "Available" },
  { name: "Ani Kusuma C", avatar: -1, initial: "A", platforms: ["tiktok", "shopee"], status: "Available" },
  { name: "Ani Kusuma E", avatar: -1, initial: "A", platforms: ["tiktok"], status: "Available" },
  { name: "Anisa Nugroho", avatar: -1, initial: "A", platforms: ["shopee"], status: "Available" },
  { name: "Anisa Nugroho B", avatar: -1, initial: "A", platforms: ["tiktok", "shopee"], status: "Available" },
];


function TeamContent() {
  const highlighted = useFakeCursor(teamData.length, 2000);

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Search + filter bar */}
      <div className="flex items-center gap-3 px-4 h-9 border-b border-border">
        <div className="flex items-center gap-1.5">
          <LiveDot />
          <span className="text-[10px] font-medium text-success"><AnimatedCount target={42} /> online</span>
        </div>
        <span className="text-[11px] text-muted-foreground"><AnimatedCount target={322} duration={1500} /> of 355 available</span>
      </div>
      <div className="flex items-center gap-3 px-4 h-9 border-b border-border">
        <div className="flex items-center gap-1.5 flex-1">
          <span className="text-[10px] text-muted-foreground">🔍</span>
          <TypingSearch text="Alya Putri" />
        </div>
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <span>All Platforms</span>
          <span className="text-[8px]">▼</span>
        </div>
      </div>
      {/* Table header */}
      <div className="flex items-center px-4 h-8 border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        <div className="flex-1">Host ↑</div>
        <div className="w-24 text-center">Platforms</div>
        <div className="w-20 text-right">Status</div>
      </div>
      {/* Rows */}
      <div className="flex-1 overflow-hidden">
        {teamData.map((host, i) => (
          <div key={i} className={`flex items-center px-4 h-10 border-b border-border transition-all duration-300 ${
            highlighted === i ? "bg-primary/5" : ""
          }`}>
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="h-6 w-6 rounded-full bg-muted-foreground/15 flex items-center justify-center text-[10px] font-medium text-muted-foreground shrink-0">
                {host.initial}
              </div>
              <span className="text-[12px] text-foreground truncate">{host.name}</span>
              {host.online && <LiveDot />}
            </div>
            <div className="w-24 flex items-center justify-center gap-1">
              {host.platforms.map((p) => <PlatformIcon key={p} type={p} />)}
            </div>
            <div className="w-20 flex justify-end">
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-success/30 text-success">{host.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Scheduling Tab ─── */
const scheduleHosts = [
  { name: "Alya Putri", contract: "30h", initial: "A", shifts: ["10–18", "10–18", "10–18", "Off", "10–18", "12–20", "Off"], hours: "40h" },
  { name: "Rani Wijaya", contract: "40h", initial: "R", shifts: ["08–17", "08–17", "08–17", "08–17", "08–17", "Off", "Off"], hours: "45h" },
  { name: "Sarah Amelia", contract: "24h", initial: "S", shifts: ["14–22", "Off", "14–22", "Off", "14–22", "Off", "Off"], hours: "24h" },
  { name: "Dina Kartika", contract: "40h", initial: "D", shifts: ["08–17", "08–17", "08–17", "08–17", "08–17", "Off", "Off"], hours: "45h" },
  { name: "Ayu Rahayu", contract: "40h", initial: "A", shifts: ["08–17", "08–17", "08–17", "08–17", "08–17", "Off", "Off"], hours: "45h" },
  { name: "Putri Sari", contract: "40h", initial: "P", shifts: ["14–22", "14–22", "14–22", "14–22", "14–22", "Off", "Off"], hours: "40h" },
  { name: "Sari Wulandari", contract: "40h", initial: "S", shifts: ["10–18", "Off", "10–18", "Off", "10–18", "12–20", "Off"], hours: "32h" },
  { name: "Maya Permata", contract: "40h", initial: "M", shifts: ["Off", "08–17", "08–17", "08–17", "08–17", "08–17", "Off"], hours: "45h" },
  { name: "Dewi Anggraeni", contract: "40h", initial: "D", shifts: ["18–22", "18–22", "18–22", "18–22", "18–22", "18–22", "Off"], hours: "24h" },
];
const dayHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function SchedulingContent() {
  const highlighted = useFakeCursor(scheduleHosts.length, 2200);

  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="px-4 pt-3 pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-foreground">Weekly Shift Patterns</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground"><AnimatedCount target={350} duration={1400} /> hosts</span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Define when each host is available to work.</p>
      </div>
      {/* Table header */}
      <div className="grid items-center px-4 h-8 border-b border-border text-[10px] text-muted-foreground" style={{ gridTemplateColumns: "140px repeat(7, 1fr) 50px" }}>
        <div>Host</div>
        {dayHeaders.map(d => <div key={d} className="text-center">{d}</div>)}
        <div className="text-right">Hrs/wk</div>
      </div>
      {/* Rows */}
      <div className="flex-1 overflow-hidden">
        {scheduleHosts.map((host, i) => (
          <div key={i} className={`grid items-center px-4 h-10 border-b border-border text-[11px] transition-all duration-300 ${
            highlighted === i ? "bg-primary/5" : ""
          }`} style={{ gridTemplateColumns: "140px repeat(7, 1fr) 50px" }}>
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-5 w-5 rounded-full bg-muted-foreground/15 flex items-center justify-center text-[9px] font-medium text-muted-foreground shrink-0">
                {host.initial}
              </div>
              <div className="min-w-0">
                <div className="text-[11px] text-foreground truncate">{host.name}</div>
                <div className="text-[9px] text-muted-foreground">{host.contract} contract</div>
              </div>
            </div>
            {host.shifts.map((shift, j) => (
              <div key={j} className="text-center">
                <span className={shift === "Off" ? "text-muted-foreground/40" : "text-success font-medium"}>{shift}</span>
              </div>
            ))}
            <div className="text-right">
              <span className="text-success font-medium">{host.hours}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Events Tab ─── */
function EventsContent() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(p => p >= 72 ? 72 : p + 1);
    }, 30);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex-1 flex flex-col min-w-0 p-4">
      {/* Active now */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Active Now</span>
        <span className="text-[10px] bg-muted-foreground/10 px-1.5 py-0.5 rounded-full text-muted-foreground">1</span>
        <LiveDot />
      </div>
      <div className="border border-border rounded-lg p-3 mb-5">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[16px]">💰</span>
            <div>
              <div className="text-[13px] font-medium text-foreground">Pay Day</div>
              <div className="text-[10px] text-muted-foreground">✦ Sale Event</div>
            </div>
          </div>
          <div className="flex gap-1.5">
            <span className="flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full bg-success/20 text-success font-medium">
              <LiveDot /> Active
            </span>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-warning/20 text-warning font-medium">Medium</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-2">
          <span>📅 2026-03-25 → 2026-03-31</span>
          <span>7d</span>
        </div>
        {/* Progress bar */}
        <div className="w-full h-1.5 rounded-full bg-muted mb-2 overflow-hidden">
          <div className="h-full rounded-full bg-success transition-all duration-100" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] text-muted-foreground">Monthly pay day period (25th–end of month). Flash sales and extra afternoon slots.</p>
          <span className="text-[9px] text-success font-medium">{progress}%</span>
        </div>
        <div className="flex gap-1.5">
          {["1.3x sessions", "#flash-sale", "#afternoon"].map(tag => (
            <span key={tag} className="text-[9px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">{tag}</span>
          ))}
        </div>
      </div>

      {/* Past events */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Past Events</span>
        <span className="text-[10px] bg-muted-foreground/10 px-1.5 py-0.5 rounded-full text-muted-foreground">3</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: "🧧", name: "Chinese New Year", type: "Religious", severity: "High", dates: "2026-02-17 → 02-23", days: "7d", desc: "CNY gifting campaign with morning special editions.", tags: ["1.5x sessions", "#gifting", "#morning"] },
          { icon: "🌙", name: "Ramadan", type: "Religious", severity: "Critical", dates: "2026-02-28 → 03-30", days: "31d", desc: "Holy month with peak evening & late-night shopping.", tags: ["1.5x sessions", "#iftar", "#sahur"] },
          { icon: "✦", name: "Wardah Beauty Week", type: "Brand Event", severity: "High", dates: "2026-03-15 → 03-21", days: "7d", desc: "Exclusive brand week with product launches.", tags: ["2x sessions", "#launch", "#exclusive"] },
        ].map(ev => (
          <div key={ev.name} className="border border-border rounded-lg p-3">
            <div className="flex items-start justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[14px]">{ev.icon}</span>
                <div>
                  <div className="text-[11px] font-medium text-foreground">{ev.name}</div>
                  <div className="text-[9px] text-muted-foreground">☆ {ev.type}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <span className="text-[8px] text-muted-foreground">Past</span>
                <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${ev.severity === "Critical" ? "bg-destructive/20 text-destructive" : "bg-warning/20 text-warning"}`}>{ev.severity}</span>
              </div>
            </div>
            <div className="text-[9px] text-muted-foreground mb-1.5">📅 {ev.dates} · {ev.days}</div>
            <p className="text-[9px] text-muted-foreground mb-2">{ev.desc}</p>
            <div className="flex flex-wrap gap-1">
              {ev.tags.map(tag => (
                <span key={tag} className="text-[8px] px-1.5 py-0.5 rounded-full border border-border text-muted-foreground">{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Studios Tab ─── */
function StudiosContent() {
  const studios = [
    {
      name: "Studio A", status: "live", location: "Jakarta · Jl. Sudirman No. 45",
      maxHosts: 4, equipment: 6, internet: "Fiber 500Mbps",
      gear: [
        { category: "Camera", items: "Sony A7IV  2× · Main + backup" },
        { category: "Lighting", items: "Ring Light 18\"  3×" },
        { category: "Audio", items: "Rode NT-USB  2×" },
        { category: "Display", items: "Teleprompter 15\" · Monitor 27\" 4K  2×" },
        { category: "Connectivity", items: "Fiber 500Mbps · Dedicated line" },
      ],
    },
    {
      name: "Studio B", status: "active", location: "Jakarta · Jl. Gatot Subroto No. 12",
      maxHosts: 3, equipment: 5, internet: "Fiber 300Mbps",
      gear: [],
    },
    {
      name: "Studio C", status: "active", location: "Jakarta · Jl. Rasuna Said No. 8",
      maxHosts: 2, equipment: 4, internet: "Fiber 200Mbps",
      gear: [],
    },
  ];

  return (
    <div className="flex-1 flex flex-col min-w-0 p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[13px] font-medium text-foreground">Studios & Locations</div>
          <div className="text-[10px] text-muted-foreground">3 studios configured · <span className="text-success font-medium">1 live session</span></div>
        </div>
        <div className="text-[10px] px-2.5 py-1 rounded border border-foreground/20 text-foreground font-medium">+ Add Studio</div>
      </div>
      <div className="space-y-3">
        {studios.map(s => (
          <div key={s.name} className="border border-border rounded-lg p-3">
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded bg-muted-foreground/10 flex items-center justify-center text-[10px]">🏢</div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-medium text-foreground">{s.name}</span>
                    {s.status === "live" ? (
                      <span className="flex items-center gap-1 text-[8px] px-1.5 py-0.5 rounded-full bg-destructive/20 text-destructive font-medium">
                        <LiveDot /> Live
                      </span>
                    ) : (
                      <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-success/20 text-success font-medium">{s.status}</span>
                    )}
                  </div>
                  <div className="text-[9px] text-muted-foreground">📍 {s.location}</div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-2 text-[9px] text-muted-foreground">
              <span>{s.maxHosts} max hosts</span>
              <span>{s.equipment} equipment items</span>
              <span>📶 {s.internet}</span>
            </div>
            {s.gear.length > 0 && (
              <div className="mt-3 border-t border-border pt-2 space-y-1.5">
                <div className="text-[10px] font-medium text-foreground mb-1">Equipment</div>
                {s.gear.map(g => (
                  <div key={g.category} className="text-[9px]">
                    <span className="text-muted-foreground uppercase tracking-wider">{g.category}</span>
                    <span className="text-foreground/80 ml-2">{g.items}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── AI Schedule Builder Modal ─── */
const quickActions = [
  { icon: "↻", title: "Same as last week", desc: "Copy last week's schedule with the same team assignments" },
  { icon: "⚡", title: "24/7 Coverage", desc: "Round-the-clock 2h sessions, same team as last week" },
  { icon: "👥", title: "Smart availability + prime time", desc: "Check availability, avoid conflicts, best performers on prime time" },
  { icon: "⏱", title: "Quick 1h sessions", desc: "Short 1-hour sessions, 4 per day across platforms" },
  { icon: "🕐", title: "Extended 2h sessions", desc: "Longer 2-hour sessions, 3 per day with breaks" },
];

function AIScheduleModal({ onClose }: { onClose: () => void }) {
  const [selectedAction, setSelectedAction] = useState(-1);
  const [typedPrompt, setTypedPrompt] = useState("");
  const [phase, setPhase] = useState<"typing" | "generating" | "result">("typing");
  const promptText = "Schedule 3 extended 2-hour sessions per day with 1-hour breaks in between, across TikTok and Shopee";

  const generatedSessions = [
    { time: "09:00–11:00", platform: "TikTok", host: "Alya Putri", studio: "Studio A", initial: "A" },
    { time: "12:00–14:00", platform: "Shopee", host: "Rani Wijaya", studio: "Studio B", initial: "R" },
    { time: "15:00–17:00", platform: "TikTok", host: "Sarah Amelia", studio: "Studio A", initial: "S" },
  ];

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      if (i < promptText.length) {
        setTypedPrompt(promptText.slice(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        setTimeout(() => setSelectedAction(4), 600);
        setTimeout(() => setPhase("generating"), 1600);
        setTimeout(() => setPhase("result"), 3400);
        setTimeout(() => onClose(), 7500);
      }
    }, 30);
    return () => clearInterval(timer);
  }, [onClose]);

  if (phase === "result") {
    return (
      <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-t-xl">
        <div className="bg-card border border-border rounded-xl shadow-2xl w-[400px] flex flex-col overflow-hidden animate-scale-in">
          {/* Success header */}
          <div className="flex items-center gap-3 p-4 pb-3 border-b border-border">
            <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center text-[16px]">✓</div>
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-foreground">Schedule Generated!</div>
              <div className="text-[10px] text-muted-foreground">21 sessions created across 7 days · 3 hosts assigned</div>
            </div>
          </div>

          {/* Generated sessions preview */}
          <div className="p-4 space-y-2.5">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Monday, Mar 23 · Preview</div>
            {generatedSessions.map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-success/20 bg-success/5 animate-fade-in" style={{ animationDelay: `${i * 200}ms` }}>
                <div className="h-7 w-7 rounded-full bg-muted-foreground/15 flex items-center justify-center text-[11px] font-medium text-muted-foreground">{s.initial}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-foreground">{s.time}</span>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${
                      s.platform === "TikTok" ? "bg-foreground/10 text-foreground/70" : "bg-destructive/15 text-destructive/70"
                    }`}>{s.platform}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground">{s.host} · {s.studio}</div>
                </div>
                <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-success/20 text-success font-medium">New</span>
              </div>
            ))}
            <div className="text-[10px] text-muted-foreground text-center pt-1">+ 18 more sessions across Tue–Sun →</div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="text-success">●</span> 3 hosts</span>
              <span className="flex items-center gap-1"><span className="text-primary">●</span> 2 studios</span>
              <span className="flex items-center gap-1"><span className="text-warning">●</span> 42h total</span>
            </div>
            <button className="text-[11px] font-medium px-3 py-1.5 rounded-lg bg-foreground text-background flex items-center gap-1.5 hover:bg-foreground/90 transition-colors">
              ✓ Apply Schedule
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-t-xl">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-[440px] max-h-[420px] flex flex-col overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 pb-3">
          <div className="h-8 w-8 rounded-lg bg-foreground/10 flex items-center justify-center text-[14px] shrink-0">✦</div>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-foreground">AI Schedule Builder</div>
            <div className="text-[10px] text-muted-foreground">Describe your schedule or upload a file for the week of <span className="font-medium text-foreground">Mar 23 – Mar 29</span></div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-[14px]">×</button>
        </div>

        <div className="flex-1 overflow-auto px-4 pb-3 space-y-3">
          {/* Describe prompt */}
          <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Describe your agenda</div>
            <div className="border border-primary/40 rounded-lg p-2.5 min-h-[48px] bg-primary/5">
              <span className="text-[11px] text-foreground leading-relaxed">
                {typedPrompt}
                <span className="inline-block w-px h-3 bg-foreground/60 ml-px animate-pulse" />
              </span>
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Quick Actions</div>
            <div className="space-y-1.5">
              {quickActions.map((action, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2.5 p-2 rounded-lg border transition-all duration-300 cursor-pointer ${
                    selectedAction === i
                      ? "border-primary/40 bg-primary/5 shadow-sm"
                      : "border-border hover:border-muted-foreground/30"
                  }`}
                >
                  <div className="h-6 w-6 rounded-lg bg-muted flex items-center justify-center text-[11px] shrink-0">{action.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-medium text-foreground">{action.title}</div>
                    <div className="text-[9px] text-muted-foreground truncate">{action.desc}</div>
                  </div>
                  {selectedAction === i && (
                    <div className="h-4 w-4 rounded-full border-2 border-primary flex items-center justify-center shrink-0">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <span className="text-[10px] text-muted-foreground">
            {phase === "generating" ? (
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Generating schedule...
              </span>
            ) : (
              "Ready to generate"
            )}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-[11px] text-muted-foreground hover:text-foreground px-3 py-1.5">Cancel</button>
            <button className={`text-[11px] font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all ${
              phase === "generating"
                ? "bg-primary/80 text-primary-foreground"
                : "bg-foreground text-background hover:bg-foreground/90"
            }`}>
              <span className="text-[12px]">✦</span> Generate Schedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export function HeroMockup() {
  const [activeTab, setActiveTab] = useState<TabKey>("sessions");
  const [showAIModal, setShowAIModal] = useState(false);
  const [scheduleGenerated, setScheduleGenerated] = useState(false);

  // Show AI modal after 12 seconds on the initial load
  useEffect(() => {
    const aiTimer = setTimeout(() => setShowAIModal(true), 12000);
    return () => clearTimeout(aiTimer);
  }, []);

  // Auto-cycle tabs every 8 seconds, pause while modal is open or schedule just generated
  useEffect(() => {
    if (showAIModal || scheduleGenerated) return;
    const tabOrder: TabKey[] = ["sessions", "team", "scheduling", "events", "studios"];
    const timer = setInterval(() => {
      setActiveTab(current => {
        const idx = tabOrder.indexOf(current);
        return tabOrder[(idx + 1) % tabOrder.length];
      });
    }, 8000);
    return () => clearInterval(timer);
  }, [showAIModal, scheduleGenerated]);

  const handleAIModalClose = () => {
    setShowAIModal(false);
    setScheduleGenerated(true);
    setActiveTab("sessions");
    // Reset after 12 seconds to loop again
    setTimeout(() => {
      setScheduleGenerated(false);
    }, 12000);
  };

  return (
    <div className="relative" style={{ overflow: "visible" }}>

      <div className="relative z-10 rounded-t-xl border border-b-0 border-border bg-card overflow-hidden min-w-0">
        <div className="flex flex-col min-h-[420px]">
          {/* Top tabs bar */}
          <div className="flex items-center gap-1 px-2 sm:px-4 py-2 border-b border-border overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative shrink-0 flex items-center gap-1.5 px-2 sm:px-2.5 h-7 rounded text-[10px] sm:text-[11px] transition-colors ${
                  activeTab === tab.key
                    ? "bg-accent text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                }`}
              >
                <span className="whitespace-nowrap">{tab.label}</span>
                {tab.count && (
                  <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                    tab.key === "sessions" && scheduleGenerated
                      ? "bg-success/20 text-success font-medium"
                      : "bg-muted-foreground/10"
                  }`}>
                    {tab.key === "sessions" && scheduleGenerated ? "21" : tab.count}
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => setShowAIModal(true)}
              className={`shrink-0 flex items-center gap-1.5 px-2 h-7 rounded text-[10px] sm:text-[11px] transition-colors ${
                scheduleGenerated
                  ? "text-success bg-success/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              }`}
            >
              <span>✦</span>
              <span className="whitespace-nowrap">AI Schedule</span>
            </button>
          </div>

          {/* Tab content */}
          <div className="animate-fade-in" key={activeTab + (scheduleGenerated ? "-gen" : "")}>
            {activeTab === "sessions" && <SessionsContent scheduleGenerated={scheduleGenerated} />}
            {activeTab === "team" && <TeamContent />}
            {activeTab === "scheduling" && <SchedulingContent />}
            {activeTab === "events" && <EventsContent />}
            {activeTab === "studios" && <StudiosContent />}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />

        {/* AI Schedule Builder overlay */}
        {showAIModal && <AIScheduleModal onClose={handleAIModalClose} />}
      </div>
    </div>
  );
}
